import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { PresentableTaskResource } from '@timelyapp/integration-cloud-api-spec';

export interface TicTasksState {
  /** Stores all tasks retrieved via getTasks */
  tasks: Array<PresentableTaskResource> | null;
}
const initialState: TicTasksState = {
  tasks: null,
};

const ticTasksSlice = createSlice({
  name: 'ticTasks',
  initialState,
  reducers: {
    // Merge incoming rows over the cached ones by id (last-write-wins per field), rather than the
    // previous first-wins dedupe. The cache is a single growing pool shared by every getTasks call
    // (initial, search, scope-narrowed, point lookups), so the same task arrives repeatedly: the
    // old `uniqBy` kept the first copy and silently pinned stale fields (e.g. completed_at,
    // timely_project_id), whereas merging lets a later fetch refresh them. Position is preserved.
    appendTasks: (state, { payload }: PayloadAction<{ tasks: Array<PresentableTaskResource> }>) => {
      const tasksById = new Map((state.tasks ?? []).map(task => [task.id, task]));
      payload.tasks.forEach(task => {
        const existingTask = tasksById.get(task.id);
        tasksById.set(task.id, existingTask ? { ...existingTask, ...task } : task);
      });
      state.tasks = Array.from(tasksById.values());
    },
  },
  selectors: {
    getTicTasks: (state: TicTasksState) => {
      return state.tasks;
    },
  },
});

export const { appendTasks } = ticTasksSlice.actions;
export const { getTicTasks } = ticTasksSlice.selectors;

export default ticTasksSlice.reducer;
